
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { IArticle, ITemplate } from '@/types';
import { getArticle } from 'api/articles';
import cx from 'classnames';
import { ArtistCardProps } from 'components/artists/ArtistCard';
import { ArtworkCardProps } from 'components/artwork/ArtworkCard';
import { Breadcrumbs } from 'components/common/Breadcrumbs';
import { Separator } from 'components/common/Separator';
import { ArticleCarousels } from 'components/editorial/articles/ArticleCarousels';
import { ArticleTemplate } from 'components/editorial/articles/ArticleTemplate';
import { BaseLayout } from 'layouts/BaseLayout';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import qs from 'qs';
import { ReactElement, useEffect } from 'react';
import { fetchFeaturedArtists, fetchFeaturedArtworks } from 'services/homepage';
import { getTemplateName } from 'utils/articles';
import { getArticleUrl } from 'utils/urls';
interface ArticleProps {
    id: number;
    initialData: {
        article: IArticle['attributes'];
    };
    url: string;
    trendingArtists?: ArtistCardProps[];
    trendingArtworks?: ArtworkCardProps[];
}
const Article = ({ initialData: { article }, url, trendingArtists, trendingArtworks, }: ArticleProps) => {
    const router = useRouter();
    useEffect(() => {
        if (router.asPath !== url) {
            router.replace(url);
        }
    }, [router, url]);
    return (<div className={cx('w-full')}>
      <Head>
        <title>{article.seo?.title}</title>

        <meta name='description' content={article.seo?.description}/>

        <meta name='og:title' content={article.seo?.title || article.title}/>
        <meta name='og:description' content={article.seo?.description}/>

        <link rel='canonical' href={`${process.env.NEXT_PUBLIC_SITE_URL}${url}`}/>
      </Head>

      <Breadcrumbs url={url} className='container px-6 sm:px-11'/>

      <Separator color='cool-300'/>

      <ArticleTemplate {...article}/>

      <ArticleCarousels article={article} has={getCarouselsMatrix(article?.template?.[0])} trendingArtists={trendingArtists} trendingArtworks={trendingArtworks}/>
    </div>);
};
Article.getLayout = (page: ReactElement) => (<BaseLayout style='pure-white'>{page}</BaseLayout>);
const getCarouselsMatrix = (template?: ITemplate | null) => {
    const defaultMatrix = {
        moreFromArtist: true,
        releases: true,
        articles: true,
        searchedArtworks: true,
        trendingArtworks: true,
        trendingArtists: true
    };
    if (!template) {
        return defaultMatrix;
    }
    switch (getTemplateName(template)) {
        case 'guide':
            return {
                moreFromArtist: false,
                releases: false,
                articles: true,
                searchedArtworks: true,
                trendingArtworks: true,
                trendingArtists: false
            };
        case 'week-ahead':
            return {
                moreFromArtist: false,
                releases: true,
                articles: true,
                searchedArtworks: false,
                trendingArtworks: true,
                trendingArtists: false
            };
        case 'artist-spotlight':
            return {
                moreFromArtist: true,
                releases: false,
                articles: true,
                searchedArtworks: false,
                trendingArtworks: false,
                trendingArtists: true
            };
        case 'good-things-come-in-three':
            return {
                moreFromArtist: true,
                releases: false,
                articles: true,
                searchedArtworks: false,
                trendingArtworks: true,
                trendingArtists: false
            };
        default:
            return defaultMatrix;
    }
};
const fetchArticle = async (id: number) => {
    const query = qs.stringify({
        populate: [
            'seo',
            'articleImage',
            'articleImage.image',
            'category',
            'tags',
            'artist',
            'template',
            'template.release',
            'template.release.image',
            'template.aboutArtist',
            'template.artistShowcase',
            'template.artistShowcase.image',
            'template.lastMeal',
            'template.twoTruthsOneLie',
            'template.workInThreeWords',
        ]
    }, {
        encodeValuesOnly: true
    });
    const articleData = await getArticle(id, query);
    return {
        article: articleData.data
    };
};
const getServerSideProps: GetServerSideProps<ArticleProps> = async ({ params, res, }) => {
    res.setHeader('Cache-Control', `public, s-maxage=${12 * 60 * 60}, stale-while-revalidate=${60 * 60}`);
    const slug = params?.slug;
    if (!slug) {
        return { notFound: true, props: { error: 'NO SLUG' } };
    }
    const id = Number(slug[slug.length - 1]);
    if (isNaN(id)) {
        return { notFound: true, props: { error: 'INVALID ID' } };
    }
    try {
        const { article } = await fetchArticle(id);
        if (!article) {
            return { notFound: true, props: { error: 'NO ARTICLE' } };
        }
        const articleSlug = article.attributes.slug;
        const category = article.attributes.category?.data?.attributes.name;
        if (!articleSlug) {
            return { notFound: true, props: { error: 'NO ARTICLE SLUG' } };
        }
        const matrix = getCarouselsMatrix(article.attributes.template?.[0]);
        const [trendingArtists, trendingArtworks] = await Promise.all([
            matrix.trendingArtists ? fetchFeaturedArtists() : [],
            matrix.trendingArtworks ? fetchFeaturedArtworks() : [],
        ]);
        return {
            props: {
                id,
                initialData: {
                    article: article.attributes
                },
                url: getArticleUrl(articleSlug, category || null, id),
                trendingArtists,
                trendingArtworks
            }
        };
    }
    catch (err) {
        console.log(err);
        return { notFound: true, props: { error: (err as Error).message, err } };
    }
};
export default Article;

    async function __Next_Translate__getServerSideProps__19788b54ab0__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/editorial/[...slug]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__19788b54ab0__ as getServerSideProps }
  